// @ts-nocheck
import React, { useState, useEffect } from "react";
import { CancellationReasonEnum } from "../../utils/enums"; 

const Icon = {
  AlertTriangle: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="12" y1="9" x2="12" y2="13" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="12" cy="17" r="0.5" fill="currentColor" strokeWidth="1"/>
    </svg>
  ),
  X: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
};

export default function CancelOrderDialog({ open, onClose, onConfirm }) {
  const [selected, setSelected] = useState("");
  const [otherText, setOtherText] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    }
  }, [open]);

  if (!open && !isVisible) return null;

  const reasons = Object.values(CancellationReasonEnum);

  const finalReason =
    selected === CancellationReasonEnum.OTHER
      ? (otherText || "").trim()
      : selected;

  const canConfirm =
    Boolean(selected) &&
    (selected !== CancellationReasonEnum.OTHER || Boolean(finalReason));

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onConfirm(finalReason);
    }, 200);
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-200 ${
        isClosing ? 'bg-black/0' : 'bg-black/60'
      }`}
      style={{ backdropFilter: isClosing ? 'blur(0px)' : 'blur(8px)' }}
      role="dialog" 
      aria-modal="true"
      onClick={handleClose}
    >
      <div 
        className={`bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isClosing 
            ? 'scale-95 opacity-0 translate-y-4' 
            : 'scale-100 opacity-100 translate-y-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient accent */}
        <div className="relative bg-gradient-to-br from-rose-500 via-rose-600 to-red-600 px-6 py-8 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="relative">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/30">
                  <Icon.AlertTriangle className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold tracking-tight">Cancel Order</h3>
                  <p className="text-rose-100 text-sm mt-0.5">This action requires confirmation</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all duration-200 ring-1 ring-white/20"
                aria-label="Close dialog"
              >
                <Icon.X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <p className="text-slate-600 mb-5 text-[15px]">
            Please select a reason for canceling this order. This information helps improve our service.
          </p>

          <div className="space-y-4">
            {/* Reason selector with custom styling */}
            <div className="relative group">
              <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                Cancellation Reason
              </label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3.5 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-rose-300 hover:ring-slate-300 outline-none bg-white appearance-none transition-all duration-200 text-slate-800 font-medium cursor-pointer shadow-sm hover:shadow"
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  autoFocus
                >
                  <option value="" className="text-slate-400">- Choose a reason -</option>
                  {reasons.map((r) => (
                    <option key={r} value={r} className="text-slate-800">{r}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-slate-400 group-hover:text-slate-600 transition-colors">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0l-4.24-4.24a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Custom reason input with smooth animation */}
            <div 
              className={`overflow-hidden transition-all duration-300 ${
                selected === CancellationReasonEnum.OTHER 
                  ? 'max-h-32 opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}
            >
              <div className="relative">
                <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                  Custom Reason
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3.5 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-rose-300 hover:ring-slate-300 outline-none bg-white transition-all duration-200 text-slate-800 placeholder:text-slate-400 shadow-sm hover:shadow"
                  placeholder="Please specify the reason..."
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  maxLength={200}
                />
                {selected === CancellationReasonEnum.OTHER && (
                  <div className="mt-1.5 text-xs text-slate-500 flex items-center justify-between">
                    <span>Be specific to help us improve</span>
                    <span className={`font-medium ${otherText.length > 180 ? 'text-amber-600' : ''}`}>
                      {otherText.length}/200
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Warning notice */}
          {selected && (
            <div className="mt-5 p-4 rounded-xl bg-amber-50 ring-1 ring-amber-200/50 animate-fadeIn">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-amber-700 text-xs font-bold">!</span>
                </div>
                <div className="flex-1 text-sm text-amber-800">
                  <p className="font-semibold mb-0.5">Important Notice</p>
                  <p className="text-amber-700">This order will be permanently canceled and cannot be restored.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with enhanced buttons */}
        <div className="px-6 py-5 bg-slate-50/50 flex justify-end gap-3 border-t border-slate-100">
          <button 
            className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 hover:ring-slate-300 active:bg-slate-100 font-medium transition-all duration-200 shadow-sm hover:shadow"
            onClick={handleClose}
          >
            Keep Order
          </button>
          <button
            className={`inline-flex items-center justify-center rounded-xl px-6 py-2.5 text-white font-semibold transition-all duration-200 shadow-lg ${
              canConfirm
                ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 active:from-rose-700 active:to-red-700 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                : 'bg-slate-300 cursor-not-allowed opacity-60'
            }`}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            <Icon.AlertTriangle className="h-4 w-4 mr-2" />
            Confirm Cancellation
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
